import React, { useState } from 'react';
import { useUser } from '@clerk/clerk-react';
import { useNavigate } from 'react-router-dom';

const CompleteProfilePage = () => {
  const { user, isLoaded } = useUser();
  const [formData, setFormData] = useState({
    name: '',
    gdprConsent: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Pre-fill name from Clerk user if available
  React.useEffect(() => {
    if (isLoaded && user) {
      const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim();
      if (fullName) {
        setFormData(prev => ({ ...prev, name: fullName }));
      }
    }
  }, [isLoaded, user]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.gdprConsent) {
      setError('Please accept the GDPR consent to proceed');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Parse name into first and last name
      const nameParts = formData.name.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Update Clerk user with name and metadata
      await user.update({
        firstName,
        lastName,
      });

      // Update public metadata for profile completion status
      await user.update({
        unsafeMetadata: {
          gdprConsent: true,
          isProfileComplete: true
        }
      });

      // Reload user data to get updated metadata
      await user.reload();

      navigate('/dashboard');
    } catch (error) {
      console.error('Profile update error:', error);
      setError(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8"
      style={{
        backgroundImage: 'url(/background_1080.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      <div className="max-w-md w-full space-y-8 bg-white rounded-lg shadow-lg p-8">
        <div className="flex flex-col items-center">
          <img
            src="/logo/android-chrome-512x512.png"
            alt="Vocabulares"
            style={{ width: '100px', height: '100px', objectFit: 'contain', marginBottom: '16px' }}
          />
          <h2 className="text-center text-3xl font-extrabold text-gray-900">
            Complete Your Profile
          </h2>
        </div>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative">
            {error}
          </div>
        )}
        <form onSubmit={handleSubmit} className="mt-8 space-y-6">
          <div className="rounded-md shadow-sm">
            <div>
              <label htmlFor="name" className="sr-only">Name</label>
              <input
                id="name"
                name="name"
                type="text"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 focus:z-10 sm:text-sm"
                placeholder="Your Name"
                value={formData.name}
                onChange={handleInputChange}
              />
            </div>
          </div>

          <div className="flex items-center">
            <input
              id="gdprConsent"
              name="gdprConsent"
              type="checkbox"
              className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
              checked={formData.gdprConsent}
              onChange={handleInputChange}
            />
            <label htmlFor="gdprConsent" className="ml-2 block text-sm text-gray-900">
              I agree to the processing of my personal data according to the GDPR
            </label>
          </div>

          <div>
            <button
              type="submit"
              disabled={!formData.gdprConsent || loading}
              className={`group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${formData.gdprConsent && !loading ? 'bg-indigo-600 hover:bg-indigo-700 cursor-pointer' : 'bg-indigo-400 opacity-60 cursor-not-allowed'}`}
            >
              {loading ? 'Saving...' : 'Save Profile'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompleteProfilePage;
